// ============================================================
// BILLING DETAILS FORM — dati di fatturazione, raccolti prima di Stripe
// ============================================================
// Stripe non valida né chiede codice fiscale ai clienti privati (il suo
// tax_id_collection copre solo la partita IVA business, dietro un checkbox
// "sto acquistando come attività"). Se un cliente inserisse un dato errato
// dentro Stripe non ci sarebbe modo di rintracciarlo dopo il pagamento per
// farselo ridare — specialmente chi si è registrato via social senza altri
// dati salvati. Per questo la raccolta/validazione avviene qui, con
// feedback live (debounced) invece che solo al submit.
//
// Nota fiscale: per le società di persone/capitali il codice fiscale
// COINCIDE con la partita IVA (stesse 11 cifre) — solo le ditte individuali
// e i liberi professionisti hanno un codice fiscale personale distinto
// (16 caratteri). Da qui il checkbox "coincide con la partita IVA".

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useDebounce } from '../../../hooks/useDebounce';
import type {
  BillingDetails,
  BillingType,
} from '../../../services/interfaces/IOrderService';
import {
  sanitizeVatNumber,
  validateCodiceFiscale,
  validatePartitaIva,
} from '../../../utils/italianTaxCodes';
import './BillingDetailsForm.css';

export interface BillingDetailsFormHandle {
  // Valida sincronicamente lo stato corrente, mostra tutti gli errori
  // (anche sui campi non ancora "touched") e ritorna il payload pronto per
  // l'API solo se tutto è valido — altrimenti null.
  validate: () => BillingDetails | null;
}

interface BillingDetailsFormProps {
  initialFirstName?: string;
  initialLastName?: string;
}

interface FieldState {
  billingType: BillingType;
  firstName: string;
  lastName: string;
  businessName: string;
  vatNumber: string;
  fiscalCode: string;
  sameFiscalCodeAsVat: boolean;
  address: string;
  zip: string;
  city: string;
  province: string;
}

const REQUIRED_FIELDS_PRIVATO = [
  'firstName',
  'lastName',
  'fiscalCode',
  'address',
  'zip',
  'city',
  'province',
];
const REQUIRED_FIELDS_ATTIVITA = [
  'businessName',
  'vatNumber',
  'fiscalCode',
  'address',
  'zip',
  'city',
  'province',
];

function computeErrors(f: FieldState): Record<string, string> {
  const errors: Record<string, string> = {};

  if (f.billingType === 'privato') {
    if (!f.firstName.trim()) errors.firstName = 'Il nome è obbligatorio.';
    if (!f.lastName.trim()) errors.lastName = 'Il cognome è obbligatorio.';
    if (!validateCodiceFiscale(f.fiscalCode))
      errors.fiscalCode = 'Codice fiscale non valido.';
  } else {
    if (!f.businessName.trim())
      errors.businessName = 'La ragione sociale è obbligatoria.';
    if (!validatePartitaIva(f.vatNumber))
      errors.vatNumber = 'Partita IVA non valida: servono 11 cifre corrette.';
    if (!f.sameFiscalCodeAsVat && !validateCodiceFiscale(f.fiscalCode))
      errors.fiscalCode = 'Codice fiscale non valido.';
  }

  if (!f.address.trim()) errors.address = "L'indirizzo è obbligatorio.";
  if (!f.zip.trim()) errors.zip = 'Il CAP è obbligatorio.';
  if (!f.city.trim()) errors.city = 'La città è obbligatoria.';
  if (!/^[A-Za-z]{2}$/.test(f.province.trim()))
    errors.province = 'Sigla provincia (es. MI).';

  return errors;
}

function buildPayload(f: FieldState): BillingDetails {
  const fiscalCode = (
    f.billingType === 'attivita' && f.sameFiscalCodeAsVat
      ? f.vatNumber
      : f.fiscalCode
  )
    .trim()
    .toUpperCase();

  return f.billingType === 'privato'
    ? {
        type: 'privato',
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim(),
        fiscalCode,
        address: f.address.trim(),
        zip: f.zip.trim(),
        city: f.city.trim(),
        province: f.province.trim().toUpperCase(),
      }
    : {
        type: 'attivita',
        businessName: f.businessName.trim(),
        vatNumber: f.vatNumber.trim(),
        fiscalCode,
        address: f.address.trim(),
        zip: f.zip.trim(),
        city: f.city.trim(),
        province: f.province.trim().toUpperCase(),
      };
}

// ── Campo di testo con stato live (neutro / valido / non valido) ──
const BillingField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlurField: () => void;
  status: '' | 'is-valid' | 'is-invalid';
  error?: string;
  maxLength?: number;
  autoComplete?: string;
}> = ({
  label,
  value,
  onChange,
  onBlurField,
  status,
  error,
  maxLength,
  autoComplete,
}) => (
  <div className={`billing-field ${status}`}>
    <label>{label}</label>
    <div className="billing-field-input-wrap">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlurField}
        maxLength={maxLength}
        autoComplete={autoComplete}
      />
      {status === 'is-valid' && (
        <i className="bi bi-check-circle-fill billing-field-icon valid" />
      )}
      {status === 'is-invalid' && (
        <i className="bi bi-exclamation-circle-fill billing-field-icon invalid" />
      )}
    </div>
    {status === 'is-invalid' && error && (
      <span className="billing-field-error">{error}</span>
    )}
  </div>
);

const BillingDetailsForm = forwardRef<
  BillingDetailsFormHandle,
  BillingDetailsFormProps
>(({ initialFirstName = '', initialLastName = '' }, ref) => {
  const [billingType, setBillingType] = useState<BillingType>('privato');
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [businessName, setBusinessName] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [fiscalCode, setFiscalCode] = useState('');
  const [sameFiscalCodeAsVat, setSameFiscalCodeAsVat] = useState(true);
  const [address, setAddress] = useState('');
  const [zip, setZip] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const snapshot = useMemo<FieldState>(
    () => ({
      billingType,
      firstName,
      lastName,
      businessName,
      vatNumber,
      fiscalCode,
      sameFiscalCodeAsVat,
      address,
      zip,
      city,
      province,
    }),
    [
      billingType,
      firstName,
      lastName,
      businessName,
      vatNumber,
      fiscalCode,
      sameFiscalCodeAsVat,
      address,
      zip,
      city,
      province,
    ]
  );

  // Validazione "a runtime": ricalcolata 400ms dopo l'ultima modifica, così
  // gli errori non lampeggiano ad ogni singolo tasto premuto.
  const debouncedSnapshot = useDebounce(snapshot, 400);
  useEffect(() => {
    setErrors(computeErrors(debouncedSnapshot));
  }, [debouncedSnapshot]);

  // Il tipo (privato/attività) e il checkbox "coincide con la P.IVA" sono
  // toggle discreti, non testo digitato — ricalcolare subito (senza aspettare
  // i 400ms del debounce sopra) evita che per un istante restino visibili
  // bollini/errori riferiti al set di campi precedente.
  useEffect(() => {
    setErrors(computeErrors(snapshot));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingType, sameFiscalCodeAsVat]);

  const markTouched = (field: string) =>
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));

  useImperativeHandle(ref, () => ({
    validate: () => {
      const freshErrors = computeErrors(snapshot);
      setErrors(freshErrors);

      const requiredFields =
        billingType === 'privato'
          ? REQUIRED_FIELDS_PRIVATO
          : REQUIRED_FIELDS_ATTIVITA;
      setTouched((prev) => {
        const next = { ...prev };
        requiredFields.forEach((f) => {
          next[f] = true;
        });
        return next;
      });

      return Object.keys(freshErrors).length === 0
        ? buildPayload(snapshot)
        : null;
    },
  }));

  const statusOf = (field: string): '' | 'is-valid' | 'is-invalid' => {
    if (!touched[field]) return '';
    return errors[field] ? 'is-invalid' : 'is-valid';
  };

  return (
    <div className="billing-form">
      <h2 className="billing-form-title">
        <i className="bi bi-receipt-cutoff me-2" />
        Dati di fatturazione
      </h2>

      <div className="billing-type-toggle">
        <button
          type="button"
          className={billingType === 'privato' ? 'active' : ''}
          onClick={() => setBillingType('privato')}
        >
          <i className="bi bi-person-fill me-2" />
          Privato
        </button>
        <button
          type="button"
          className={billingType === 'attivita' ? 'active' : ''}
          onClick={() => setBillingType('attivita')}
        >
          <i className="bi bi-building me-2" />
          Attività
        </button>
      </div>

      {billingType === 'privato' ? (
        <>
          <div className="billing-row">
            <BillingField
              label="Nome"
              value={firstName}
              onChange={setFirstName}
              onBlurField={() => markTouched('firstName')}
              status={statusOf('firstName')}
              error={errors.firstName}
              autoComplete="given-name"
            />
            <BillingField
              label="Cognome"
              value={lastName}
              onChange={setLastName}
              onBlurField={() => markTouched('lastName')}
              status={statusOf('lastName')}
              error={errors.lastName}
              autoComplete="family-name"
            />
          </div>

          <BillingField
            label="Codice fiscale"
            value={fiscalCode}
            onChange={(v) => setFiscalCode(v.toUpperCase())}
            onBlurField={() => markTouched('fiscalCode')}
            status={statusOf('fiscalCode')}
            error={errors.fiscalCode}
            maxLength={16}
          />
        </>
      ) : (
        <>
          <BillingField
            label="Ragione sociale"
            value={businessName}
            onChange={setBusinessName}
            onBlurField={() => markTouched('businessName')}
            status={statusOf('businessName')}
            error={errors.businessName}
            autoComplete="organization"
          />

          <BillingField
            label="Partita IVA"
            value={vatNumber}
            onChange={(v) => setVatNumber(sanitizeVatNumber(v))}
            onBlurField={() => markTouched('vatNumber')}
            status={statusOf('vatNumber')}
            error={errors.vatNumber}
            maxLength={11}
          />

          <label className="billing-checkbox">
            <input
              type="checkbox"
              checked={sameFiscalCodeAsVat}
              onChange={(e) => setSameFiscalCodeAsVat(e.target.checked)}
            />
            Il codice fiscale coincide con la partita IVA
          </label>

          {!sameFiscalCodeAsVat && (
            <BillingField
              label="Codice fiscale"
              value={fiscalCode}
              onChange={(v) => setFiscalCode(v.toUpperCase())}
              onBlurField={() => markTouched('fiscalCode')}
              status={statusOf('fiscalCode')}
              error={errors.fiscalCode}
              maxLength={16}
            />
          )}
        </>
      )}

      <BillingField
        label="Indirizzo (via e civico)"
        value={address}
        onChange={setAddress}
        onBlurField={() => markTouched('address')}
        status={statusOf('address')}
        error={errors.address}
        autoComplete="address-line1"
      />

      <div className="billing-row billing-row-3">
        <BillingField
          label="CAP"
          value={zip}
          onChange={setZip}
          onBlurField={() => markTouched('zip')}
          status={statusOf('zip')}
          error={errors.zip}
          autoComplete="postal-code"
        />
        <BillingField
          label="Città"
          value={city}
          onChange={setCity}
          onBlurField={() => markTouched('city')}
          status={statusOf('city')}
          error={errors.city}
          autoComplete="address-level2"
        />
        <BillingField
          label="Prov."
          value={province}
          onChange={(v) => setProvince(v.toUpperCase())}
          onBlurField={() => markTouched('province')}
          status={statusOf('province')}
          error={errors.province}
          maxLength={2}
        />
      </div>
    </div>
  );
});

BillingDetailsForm.displayName = 'BillingDetailsForm';

export default BillingDetailsForm;
